import { userCanSeeBestPrice, userCanSeeBestPriceOnProductCard } from '@ab-core-functions/user-permission';
import type { OfferPosition } from '@ab-core/graphql/dist';
import { useBestPrices, useProductsAvailabilitiesWithChannel } from '@ab-core/hooks-v2';
import getActiveCart from '@ab-core/hooks/cartNew/activeCart/useActiveCart';
import type { DefaultScalableProductCardProps } from '../cardLayout';
import { fallBackPosition, fallBackQuantityUnit, fallBackSalesUnit } from './fallBackStates';

export const useCartOfferLineItemToScalableProduct = (
    offerPositions?: Array<OfferPosition>
): Array<DefaultScalableProductCardProps['product']> => {
    const hasBestPrice = userCanSeeBestPrice();
    const hasBestPriceOnProductCard = userCanSeeBestPriceOnProductCard();
    const { activeCart } = getActiveCart();
    const skus: Array<string> = [];

    offerPositions?.forEach((position) => {
        if (position?.sku) {
            skus.push(position.sku);
        }
    });

    const { productsAvailabilitiesWithChannel } = useProductsAvailabilitiesWithChannel(
        {
            skus,
            cartId: activeCart?.id
        },
        !activeCart
    );
    const { bestPrices } = useBestPrices(skus, !hasBestPrice || !hasBestPriceOnProductCard || skus.length === 0);
    const productList: Array<DefaultScalableProductCardProps['product']> = [];

    offerPositions?.forEach((position) => {
        if (!position) {
            return;
        }

        productList.push({
            defaultQuantity: position.quantity || position.salesUnit || fallBackSalesUnit,

            defaultPositionText: position.positionText || '',
            sku: position.sku || '',
            information: { reach: false },
            tags: {
                product: {
                    isAbakus: false,
                    isAbakusPlus: false,
                    topProduct: false,
                    isTecselect: false,
                    isPromotion: false
                }
            },
            ids: {
                articleNumber: position.sku,
                ean: position.ean
            },
            image: {
                imgSrc: position.imageThumbnailUrl || '',
                sku: position.sku,
                position: fallBackPosition
            },
            name: {
                text: `${position?.posNumber || ''} | ${position?.name || ''}`,
                sku: position.sku,
                position: fallBackPosition
            },
            externalProductPrice: {
                ...position?.priceObject,
                priceObject: {
                    ...position?.priceObject?.priceObject,
                    materialPriceSurcharges: position?.priceObject?.materialPriceSurcharges
                }
            },
            additionalLinks: {
                sku: position.sku,
                hasAccessories: false,
                hasAlternatives: false
            },
            prices: {
                quantityUnit: position.quantityUnit || fallBackQuantityUnit,
                salesUnit: position.salesUnit || fallBackSalesUnit
            },
            availability: {
                availability: productsAvailabilitiesWithChannel?.find((available) => available.sku === position.sku),
                quantityUnit: position.quantityUnit || fallBackQuantityUnit
            },
            order: {
                bestPrice: bestPrices?.find((bestPrice) => bestPrice.sku === position.sku),
                quantityUnit: position.quantityUnit || fallBackQuantityUnit,
                salesUnit: position.salesUnit || fallBackSalesUnit,
                sku: position.sku || '',
                isUnqualifiedContractProduct: false,
                chargeNr: undefined,
                packagingSize: '',
                position: fallBackPosition,
                hidePrice: position.hidePrice === true,
                isPackageItem: position.isPackageItem === true
            },
            comparison: {
                sku: position.sku,
                title: position.name,
                imageSrc: position.imageThumbnailUrl || ''
            }
        });
    });

    return productList;
};
